// Sepet sync domain mantığı — client + server + admin tek kaynak.
// ownerKey: girişlide küçük harf e-posta, misafirde `guest:<uuid-v4>`.

import { MAX_ITEMS_PER_ORDER, MAX_QTY_PER_LINE } from "@/lib/orders";
import { foldCouponCode } from "@/lib/coupons";

export type SyncCartItem = {
  slug: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  variant?: string;
};

export type CartPayload = {
  items: SyncCartItem[];
  coupon: string | null;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,80}$/;

export function isGuestKey(ownerKey: string): boolean {
  return ownerKey.startsWith("guest:");
}

/** ownerKey normalize et — geçersizse null (401/400 kararı route'ta). */
export function normalizeOwnerKey(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const v = raw.trim();
  if (v.startsWith("guest:")) {
    const id = v.slice(6).trim();
    return UUID_RE.test(id) ? `guest:${id.toLowerCase()}` : null;
  }
  const email = v.toLowerCase();
  return EMAIL_RE.test(email) && email.length <= 254 ? email : null;
}

export function newGuestKey(): string {
  return `guest:${crypto.randomUUID()}`;
}

/**
 * Sepet/favori sahip çözümleme — girişlide session e-postası, misafirde
 * SADECE `guest:<uuid>` (başkasının e-postasıyla başkasının verisini
 * okuma/yazma = IDOR engellenir). Geçersizse null (401 kararı route'ta).
 */
export function resolveCartOwner(sessionEmail: string | undefined, raw: unknown): string | null {
  if (sessionEmail) return sessionEmail.trim().toLowerCase();
  const owner = normalizeOwnerKey(raw);
  return owner && isGuestKey(owner) ? owner : null;
}

/**
 * Misafir → giriş geçişi: client eski `guest:<uuid>` anahtarını gönderirse
 * taşınacak satırı döner, yoksa null.
 */
export function resolveMigrateFrom(sessionEmail: string | undefined, rawOwnerKey: unknown): string | null {
  if (!sessionEmail || typeof rawOwnerKey !== "string") return null;
  if (!isGuestKey(rawOwnerKey) || rawOwnerKey === sessionEmail) return null;
  return normalizeOwnerKey(rawOwnerKey);
}

/** Sync payload'unu doğrula + normalize et. Hata mesajı döner (ok ise null). */
export function validateCartPayload(body: unknown): { error?: string; cart?: CartPayload } {
  if (typeof body !== "object" || body === null) return { error: "Geçersiz sepet" };
  const b = body as { items?: unknown; coupon?: unknown };

  if (!Array.isArray(b.items)) return { error: "Geçersiz sepet" };
  if (b.items.length > MAX_ITEMS_PER_ORDER) return { error: "Sepette en fazla 50 satır olur" };

  const items: SyncCartItem[] = [];
  for (const raw of b.items) {
    if (typeof raw !== "object" || raw === null) return { error: "Geçersiz sepet satırı" };
    const r = raw as Record<string, unknown>;
    const slug = typeof r.slug === "string" ? r.slug.trim().toLowerCase() : "";
    const name = typeof r.name === "string" ? r.name.trim().slice(0, 120) : "";
    const price = Number(r.price);
    const quantity = Math.floor(Number(r.quantity));
    if (!SLUG_RE.test(slug)) return { error: `Geçersiz ürün: ${slug || "?"}` };
    if (!name) return { error: "Geçersiz ürün adı" };
    if (!Number.isFinite(price) || price < 0 || price > 500_000) return { error: `Geçersiz fiyat: ${name}` };
    if (!Number.isFinite(quantity) || quantity < 1 || quantity > MAX_QTY_PER_LINE) {
      return { error: `Geçersiz adet: ${name}` };
    }
    items.push({
      slug,
      name,
      price: Math.round(price * 100) / 100,
      quantity,
      ...(typeof r.image === "string" && r.image ? { image: r.image.slice(0, 500) } : {}),
      ...(typeof r.variant === "string" && r.variant.trim() ? { variant: r.variant.trim().slice(0, 40) } : {}),
    });
  }

  let coupon: string | null = null;
  if (typeof b.coupon === "string" && b.coupon.trim()) {
    coupon = foldCouponCode(b.coupon).slice(0, 32);
    if (!/^[A-Z0-9]{2,32}$/.test(coupon)) return { error: "Geçersiz kupon kodu" };
  }

  return { cart: { items, coupon } };
}

/** Ham sepet satırlarını temiz listeye çevir — hatalı satırlar atılır (GET yolu). */
export function normalizeCartItems(raw: unknown): SyncCartItem[] {
  if (!Array.isArray(raw)) return [];
  const items: SyncCartItem[] = [];
  for (const entry of raw.slice(0, MAX_ITEMS_PER_ORDER)) {
    if (typeof entry !== "object" || entry === null) continue;
    const r = entry as Record<string, unknown>;
    const slug = typeof r.slug === "string" ? r.slug.trim().toLowerCase() : "";
    const name = typeof r.name === "string" ? r.name.trim().slice(0, 120) : "";
    const price = Number(r.price);
    const quantity = Math.floor(Number(r.quantity));
    if (!SLUG_RE.test(slug)) continue;
    if (!name) continue;
    if (!Number.isFinite(price) || price < 0 || price > 500_000) continue;
    if (!Number.isFinite(quantity) || quantity < 1 || quantity > MAX_QTY_PER_LINE) continue;
    items.push({
      slug,
      name,
      price: Math.round(price * 100) / 100,
      quantity,
      ...(typeof r.image === "string" && r.image ? { image: r.image.slice(0, 500) } : {}),
      ...(typeof r.variant === "string" && r.variant.trim() ? { variant: r.variant.trim().slice(0, 40) } : {}),
    });
  }
  return items;
}

/** Ham kuponu temiz koda çevir — geçersizse null (GET yolu). */
export function normalizeCouponCode(raw: unknown): string | null {
  if (typeof raw !== "string" || !raw.trim()) return null;
  const coupon = foldCouponCode(raw).slice(0, 32);
  return /^[A-Z0-9]{2,32}$/.test(coupon) ? coupon : null;
}
